import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, MapPin, Users, Search, Minus, Plus } from "lucide-react";
import { format } from "date-fns";
import { fr as dfr, enUS } from "date-fns/locale";

export default function SearchWidget({ compact = false }) {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const loc = lang === "fr" ? dfr : enUS;
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [dest, setDest] = useState("Rouen");

  const fmt = (d) => (d ? format(d, "dd MMM", { locale: loc }) : null);

  const submit = () => {
    const params = new URLSearchParams();
    params.set("dest", dest);
    params.set("guests", guests);
    if (range.from) params.set("checkin", format(range.from, "yyyy-MM-dd"));
    if (range.to) params.set("checkout", format(range.to, "yyyy-MM-dd"));
    nav(`/logements?${params.toString()}`);
  };

  return (
    <div data-testid="search-widget"
      className="bg-white/90 backdrop-blur-xl backdrop-saturate-150 border border-white/50 shadow-[0_8px_40px_rgba(0,0,0,0.14)] rounded-lg p-3 md:p-2.5 flex flex-col md:flex-row md:items-stretch gap-2 md:gap-0">
      {/* Destination */}
      <div className="flex items-center gap-3 px-4 py-3 md:py-2 flex-1 md:border-r border-nj-olive">
        <MapPin className="w-5 h-5 text-nj-primary shrink-0" />
        <div className="w-full">
          <label className="block text-[10px] uppercase tracking-wider text-nj-muted font-semibold">{t("common.destination")}</label>
          <input data-testid="search-destination" value={dest} onChange={(e) => setDest(e.target.value)}
            className="w-full bg-transparent outline-none text-sm font-medium text-nj-text" />
        </div>
      </div>

      {/* Dates */}
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="search-dates" className="flex items-center gap-3 px-4 py-3 md:py-2 flex-1 md:border-r border-nj-olive text-left hover:bg-nj-olive/40 rounded-sm transition-colors">
            <CalendarDays className="w-5 h-5 text-nj-primary shrink-0" />
            <div>
              <span className="block text-[10px] uppercase tracking-wider text-nj-muted font-semibold">{t("common.checkin")} — {t("common.checkout")}</span>
              <span className="text-sm font-medium text-nj-text">
                {range.from ? `${fmt(range.from)} ${range.to ? "→ " + fmt(range.to) : ""}` : "—"}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="range" selected={range} onSelect={(r) => setRange(r || {})} numberOfMonths={1}
            disabled={{ before: new Date() }} locale={loc} />
        </PopoverContent>
      </Popover>

      {/* Guests */}
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="search-guests" className="flex items-center gap-3 px-4 py-3 md:py-2 md:w-44 text-left hover:bg-nj-olive/40 rounded-sm transition-colors">
            <Users className="w-5 h-5 text-nj-primary shrink-0" />
            <div>
              <span className="block text-[10px] uppercase tracking-wider text-nj-muted font-semibold">{t("common.guests")}</span>
              <span className="text-sm font-medium text-nj-text">{guests} {guests > 1 ? t("common.guests") : t("common.guest")}</span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-4" align="end">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t("common.guests")}</span>
            <div className="flex items-center gap-3">
              <button data-testid="guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-8 h-8 rounded-full border border-nj-olive flex items-center justify-center hover:border-nj-primary"><Minus className="w-4 h-4" /></button>
              <span className="w-6 text-center font-semibold">{guests}</span>
              <button data-testid="guests-plus" onClick={() => setGuests(Math.min(16, guests + 1))} className="w-8 h-8 rounded-full border border-nj-olive flex items-center justify-center hover:border-nj-primary"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <button data-testid="search-submit" onClick={submit}
        className="bg-nj-primary hover:bg-nj-primaryHover text-white font-semibold rounded-md px-6 py-3.5 md:py-2 flex items-center justify-center gap-2 transition-colors">
        <Search className="w-5 h-5" /> <span className={compact ? "md:hidden lg:inline" : ""}>{t("common.search")}</span>
      </button>
    </div>
  );
}
